import { useMemo, useState } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  CheckboxSize,
  Show
} from "shared/components";
import { BaseInput, InputSize } from "shared/components/forms/BaseInput";
import type { ValueFilterOption } from "../../utils/columnFilters";

// Partner-wide lists (units) can hold thousands of entries; render a window and let search narrow it.
const MAX_RENDERED_OPTIONS = 200;

// When the options cover every application, ticking them all filters nothing out: apply it as
// "no filter" rather than put every id into the URL and the request.
const selectsEverything = (draft: string[], options: ValueFilterOption[]) => {
  if (!options.length || draft.length < options.length) return false;
  const picked = new Set(draft);
  return options.every((option) => picked.has(option.value));
};

const filterOptionsBySearch = (
  options: ValueFilterOption[],
  search: string
) => {
  const needle = search.trim().toLowerCase();
  if (!needle) return options;
  return options.filter((option) =>
    option.label.toLowerCase().includes(needle)
  );
};

// HTML ids may not contain whitespace; column titles ("Assigned to") and option values can.
const toIdPart = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-");

export const ValuesFilterPanel = ({
  idPrefix,
  title,
  options,
  selected,
  searchable,
  coversEveryApplication = false,
  onApply,
  onCancel
}: {
  /** Stable id base, the column id (e.g. "assigned_to"). Defaults to the title. */
  idPrefix?: string;
  title: string;
  options: ValueFilterOption[];
  selected: string[];
  searchable: boolean;
  coversEveryApplication?: boolean;
  onApply: (values: string[]) => void;
  onCancel: () => void;
}) => {
  const idBase = `column-filter-${toIdPart(idPrefix ?? title)}`;
  const [draft, setDraft] = useState<string[]>(selected);
  const [search, setSearch] = useState("");

  const visibleOptions = useMemo(
    () => filterOptionsBySearch(options, search),
    [options, search]
  );

  const toggle = (value: string) =>
    setDraft((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );

  // Select all acts on what the search shows, like a spreadsheet's filtered list.
  const selectAllVisible = () =>
    setDraft((prev) =>
      Array.from(new Set([...prev, ...visibleOptions.map((o) => o.value)]))
    );

  return (
    <div className="w-[280px] space-y-3 p-4">
      <p className="text-sm font-bold text-content-1000">{title}</p>

      <Show if={searchable}>
        <BaseInput
          id={`${idBase}-search`}
          autoComplete="off"
          placeholder="Search..."
          value={search}
          onChange={(value) => setSearch(String(value))}
          size={InputSize.SM}
          className="!mb-0"
        />
      </Show>

      <div className="flex items-center justify-between">
        <button
          type="button"
          className="text-xs font-bold text-primary-500"
          onClick={selectAllVisible}
        >
          Select all
        </button>
        <button
          type="button"
          className="text-xs font-bold text-content-500"
          onClick={() => setDraft([])}
        >
          Clear
        </button>
      </div>

      {/* Checkbox renders an inline <label>, which ignores space-y margins: a flex column
          makes each row a block so the gap applies. */}
      <div className="-mx-1 flex max-h-[240px] flex-col gap-0.5 overflow-y-auto">
        <Show if={!visibleOptions.length}>
          <p className="px-1 text-sm text-content-400">No options available</p>
        </Show>
        {visibleOptions.slice(0, MAX_RENDERED_OPTIONS).map((option) => (
          <Checkbox
            key={option.value}
            id={`${idBase}-${toIdPart(option.value)}`}
            name={option.value}
            size={CheckboxSize.SM}
            checked={draft.includes(option.value)}
            label={
              option.count === undefined
                ? option.label
                : `${option.label} (${option.count})`
            }
            wrapperClassName="block cursor-pointer rounded-md px-1 py-1.5 hover:bg-alpha-40"
            labelClassName="whitespace-normal text-sm text-content-1000"
            onChange={() => toggle(option.value)}
          />
        ))}
        <Show if={visibleOptions.length > MAX_RENDERED_OPTIONS}>
          <p className="text-xs text-content-400">
            Showing {MAX_RENDERED_OPTIONS} of {visibleOptions.length}. Search to
            narrow the list.
          </p>
        </Show>
      </div>

      <div className="flex justify-end gap-2 border-t border-alpha-40 pt-3">
        <Button size={ButtonSize.SM} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.PRIMARY}
          onClick={() =>
            onApply(
              coversEveryApplication && selectsEverything(draft, options)
                ? []
                : draft
            )
          }
        >
          Apply
        </Button>
      </div>
    </div>
  );
};
